import config from '../config';
import {
  authHeader,
  customFetch,
  jsonHeader,
  handleResponse,
  commonRequestOptions,
  subjects,
} from '../helpers';

const endpointPath = config.api.API_URL + '/task';

export const taskService = {
  getAll,
  getById,
  getByConnectionId,
  getByScheduleId,
  getNextSyncByAsset,
  runMany,
  abortMany,
  create,
  update,
  destroy,
};

function getAll(signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/all`, requestOptions, signal).then(handleResponse);
}

function getById(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/${id}`, requestOptions, signal)
    .then(handleResponse)
    .then(res => {
      if (!res) return;
      subjects.currentTask.next(res?.tasks[0] || { task_id: id });
      return res;
    });
}

function getByConnectionId(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/connection/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Fetches all tasks assigned to a specific schedule.
 * @param {number} id - Schedule ID.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ tasks: Array }>}
 */
function getByScheduleId(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/schedule/${id}`, requestOptions, signal).then(handleResponse);
}

/**
 * Fetches the earliest-scheduled enabled dssync task per connection for the given asset.
 * Used by ChangelogToolbar to display the "Next sync" timestamp.
 * @param {string} objectName - One of: datasource, dataset_group, dataset, field
 * @param {number} objectId - The asset's primary key value
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ tasks: Array }>}
 */
function getNextSyncByAsset(objectName, objectId, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(
    `${endpointPath}/next_sync/${objectName}/${objectId}`,
    requestOptions,
    signal
  ).then(handleResponse);
}

/**
 * Enqueues multiple tasks for execution in a single request.
 * Tasks that are disabled, already running, or already queued are skipped and described in the
 * response message. Returns a single consolidated message suitable for a toast notification.
 * @param {number[]} taskIds - Array of task IDs to enqueue.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ ok: boolean, message: string }>}
 */
function runMany(taskIds, signal) {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), ...jsonHeader() },
    ...commonRequestOptions(),
    body: JSON.stringify({ task_ids: taskIds }),
  };
  return customFetch(`${endpointPath}/run`, requestOptions, signal).then(handleResponse);
}

/**
 * Aborts active jobs for multiple tasks in a single request.
 * Queued tasks are dequeued immediately; running tasks receive an abort signal asynchronously.
 * Tasks with no active job are silently skipped.
 * @param {number[]} taskIds - Array of task IDs to abort.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ ok: boolean, message: string }>}
 */
function abortMany(taskIds, signal) {
  const requestOptions = {
    method: 'POST',
    headers: { ...authHeader(), ...jsonHeader() },
    ...commonRequestOptions(),
    body: JSON.stringify({ task_ids: taskIds }),
  };
  return customFetch(`${endpointPath}/abort`, requestOptions, signal).then(handleResponse);
}

function create(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function destroy(items, signal) {
  const requestOptions = {
    method: 'DELETE',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}
